/**
 * Enhanced Error Handling for AudioRecorder
 * 
 * Provides user-friendly error messages, recovery suggestions,
 * and error logging capabilities.
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.8: Web Error Handling
 * 
 * @packageDocumentation
 */

import { AudioRecorderError } from './AudioRecorder.types';

/**
 * Detailed error information
 */
export interface AudioRecorderErrorDetails {
  /** Error type */
  type: AudioRecorderError;
  /** User-friendly message */
  message: string;
  /** Technical details (for logging) */
  technicalDetails?: string;
  /** Suggested actions for the user */
  suggestions: string[];
  /** Whether this error is recoverable */
  recoverable: boolean;
  /** Retry strategy if recoverable */
  retryStrategy?: 'immediate' | 'delayed' | 'manual';
  /** Whether to log this error to server */
  shouldLog: boolean;
  /** Severity level */
  severity: 'error' | 'warning' | 'info';
}

/**
 * Error catalog with user-friendly messages and recovery suggestions
 */
export const ERROR_CATALOG: Record<AudioRecorderError, Omit<AudioRecorderErrorDetails, 'technicalDetails'>> = {
  'permission-denied': {
    type: 'permission-denied',
    message: 'Microphone access is required to record audio',
    suggestions: [
      'Click the lock icon in your browser\'s address bar',
      'Select "Allow" for microphone access',
      'Refresh the page if needed',
      'Check your system microphone permissions',
    ],
    recoverable: true,
    retryStrategy: 'manual',
    shouldLog: false,
    severity: 'error',
  },

  'not-supported': {
    type: 'not-supported',
    message: 'Audio recording is not supported in your browser',
    suggestions: [
      'Use a modern browser like Chrome, Firefox, Safari, or Edge',
      'Update your browser to the latest version',
      'Ensure your browser supports the MediaRecorder API',
    ],
    recoverable: false,
    shouldLog: true,
    severity: 'error',
  },

  'device-error': {
    type: 'device-error',
    message: 'Unable to access your microphone',
    suggestions: [
      'Check if your microphone is properly connected',
      'Ensure no other app is using the microphone',
      'Try unplugging and reconnecting your microphone',
      'Restart your browser',
      'Check your system audio settings',
    ],
    recoverable: true,
    retryStrategy: 'delayed',
    shouldLog: true,
    severity: 'error',
  },

  'recording-error': {
    type: 'recording-error',
    message: 'An error occurred while recording',
    suggestions: [
      'Try recording again',
      'Check if your microphone is still connected',
      'Ensure you have enough storage space',
      'Close other apps that might be using audio',
    ],
    recoverable: true,
    retryStrategy: 'immediate',
    shouldLog: true,
    severity: 'error',
  },

  'upload-error': {
    type: 'upload-error',
    message: 'Failed to upload your recording',
    suggestions: [
      'Check your internet connection',
      'Try uploading again',
      'Ensure you\'re still signed in',
      'Check if the file size is within limits (500 MB)',
    ],
    recoverable: true,
    retryStrategy: 'delayed',
    shouldLog: true,
    severity: 'warning',
  },

  'network-error': {
    type: 'network-error',
    message: 'Network connection lost',
    suggestions: [
      'Check your internet connection',
      'Your recording is saved locally and will upload when connection is restored',
      'Try uploading manually when back online',
    ],
    recoverable: true,
    retryStrategy: 'delayed',
    shouldLog: false,
    severity: 'warning',
  },

  'unknown': {
    type: 'unknown',
    message: 'An unexpected error occurred',
    suggestions: [
      'Try again',
      'Refresh the page',
      'If the problem persists, contact support',
    ],
    recoverable: true,
    retryStrategy: 'manual',
    shouldLog: true,
    severity: 'error',
  },
};

/**
 * Create detailed error information
 */
export function createErrorDetails(
  type: AudioRecorderError,
  technicalDetails?: string
): AudioRecorderErrorDetails {
  const baseError = ERROR_CATALOG[type] || ERROR_CATALOG['unknown'];
  
  return {
    ...baseError,
    technicalDetails,
  };
}

/**
 * Parse browser error to AudioRecorderError type
 */
export function parseError(error: any): { type: AudioRecorderError; details: string } {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name?.toLowerCase() || '';

  // Permission errors
  if (
    errorName.includes('notallowederror') ||
    errorName.includes('permissiondeniederror') ||
    errorMessage.includes('permission denied') ||
    errorMessage.includes('not allowed')
  ) {
    return {
      type: 'permission-denied',
      details: `${error.name}: ${error.message}`,
    };
  }

  // Device/hardware errors
  if (
    errorName.includes('notfounderror') ||
    errorName.includes('deviceserror') ||
    errorMessage.includes('no device') ||
    errorMessage.includes('device not found') ||
    errorMessage.includes('could not start')
  ) {
    return {
      type: 'device-error',
      details: `${error.name}: ${error.message}`,
    };
  }

  // Not supported errors
  if (
    errorName.includes('notsupportederror') ||
    errorMessage.includes('not supported')
  ) {
    return {
      type: 'not-supported',
      details: `${error.name}: ${error.message}`,
    };
  }

  // Network errors
  if (
    errorName.includes('networkerror') ||
    errorMessage.includes('network') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('offline')
  ) {
    return {
      type: 'network-error',
      details: `${error.name}: ${error.message}`,
    };
  }

  // Upload-specific errors
  if (
    errorMessage.includes('upload') ||
    errorMessage.includes('quota') ||
    errorMessage.includes('rate limit') ||
    errorMessage.includes('file too large')
  ) {
    return {
      type: 'upload-error',
      details: `${error.name}: ${error.message}`,
    };
  }

  // Recording errors
  if (
    errorMessage.includes('recording') ||
    errorMessage.includes('mediarecorder') ||
    errorMessage.includes('codec')
  ) {
    return {
      type: 'recording-error',
      details: `${error.name}: ${error.message}`,
    };
  }

  // Unknown error
  return {
    type: 'unknown',
    details: error?.stack || `${error.name}: ${error.message}` || 'Unknown error',
  };
}

/**
 * Error logger for debugging and analytics
 */
export class AudioRecorderErrorLogger {
  private static logs: Array<{
    timestamp: Date;
    error: AudioRecorderErrorDetails;
    userAgent: string;
    url: string;
  }> = [];

  /**
   * Log an error
   */
  static log(error: AudioRecorderErrorDetails): void {
    const logEntry = {
      timestamp: new Date(),
      error,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.logs.push(logEntry);

    // Console logging in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[AudioRecorder Error]', {
        type: error.type,
        message: error.message,
        technical: error.technicalDetails,
        recoverable: error.recoverable,
        timestamp: logEntry.timestamp,
      });
    }

    // Send to server if configured and should log
    if (error.shouldLog && this.shouldSendToServer()) {
      this.sendToServer(logEntry).catch(err => {
        console.warn('Failed to send error log to server:', err);
      });
    }

    // Keep only last 100 errors in memory
    if (this.logs.length > 100) {
      this.logs.shift();
    }
  }

  /**
   * Get all logged errors
   */
  static getLogs(): typeof AudioRecorderErrorLogger.logs {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  static clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs as JSON
   */
  static exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Check if we should send logs to server
   */
  private static shouldSendToServer(): boolean {
    // TODO: Check user preferences, network status, etc.
    return process.env.REACT_APP_ERROR_LOGGING_ENABLED === 'true';
  }

  /**
   * Send error log to server
   */
  private static async sendToServer(logEntry: any): Promise<void> {
    // TODO: Implement actual error reporting endpoint
    const endpoint = process.env.REACT_APP_ERROR_REPORTING_URL;
    if (!endpoint) return;

    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry),
      });
    } catch (error) {
      // Silently fail - don't want error logging to cause more errors
      console.warn('Error logging failed:', error);
    }
  }
}

/**
 * Error recovery helpers
 */
export class AudioRecorderErrorRecovery {
  /**
   * Attempt automatic recovery for an error
   */
  static async attemptRecovery(
    error: AudioRecorderErrorDetails,
    retryCallback: () => Promise<void>
  ): Promise<boolean> {
    if (!error.recoverable) {
      return false;
    }

    switch (error.retryStrategy) {
      case 'immediate':
        // Retry immediately
        try {
          await retryCallback();
          return true;
        } catch (err) {
          return false;
        }

      case 'delayed':
        // Wait 2 seconds then retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          await retryCallback();
          return true;
        } catch (err) {
          return false;
        }

      case 'manual':
      default:
        // Requires user intervention
        return false;
    }
  }

  /**
   * Check if error recovery should be attempted
   */
  static shouldAttemptRecovery(
    error: AudioRecorderErrorDetails,
    attemptCount: number,
    maxAttempts: number = 3
  ): boolean {
    return (
      error.recoverable &&
      error.retryStrategy !== 'manual' &&
      attemptCount < maxAttempts
    );
  }
}

/**
 * Format error for display
 */
export function formatErrorMessage(error: AudioRecorderErrorDetails): string {
  return error.message;
}

/**
 * Format error suggestions for display
 */
export function formatErrorSuggestions(error: AudioRecorderErrorDetails): string[] {
  return error.suggestions;
}

/**
 * Get error icon emoji
 */
export function getErrorIcon(error: AudioRecorderErrorDetails): string {
  switch (error.type) {
    case 'permission-denied':
      return '🔒';
    case 'not-supported':
      return '⚠️';
    case 'device-error':
      return '🎤';
    case 'recording-error':
      return '⚠️';
    case 'upload-error':
      return '☁️';
    case 'network-error':
      return '📡';
    case 'unknown':
    default:
      return '❌';
  }
}

